import type { FC } from 'react';
import { useAtom } from 'jotai';
import { InlineLoading, InlineNotification } from '@carbon/react';
import { formatResource } from 'utils/misc';
import type { ActionNotice } from 'types/area-workspace';
import { AreaWorkspaceContent } from './area-workspace-content';
import {
  useAreaWorkspaceAreaAtom,
  useAreaWorkspaceResourceAtom,
} from './atoms';

interface AreaWorkspaceShellProps {
  /** The workspace's own href, `/workspaces/:id`. */
  href: string;
  // Leaves the workspace for the area it belongs to, carrying a confirmation when there
  // is one.
  onLeave: (areaId: number, notice?: ActionNotice) => void;
}

// Host for one Area Workspace: fetches the envelope, then the area it links to. The
// href comes off the workspace, so the loads are ordered and the link stays the BE's.
export const AreaWorkspaceShell: FC<AreaWorkspaceShellProps> = ({
  href,
  onLeave,
}) => {
  // Writing the atom refetches: a verdict is posted elsewhere and answers with the
  // event, so the envelope is re-read to show it.
  const [workspaceResource, refetchWorkspace] = useAtom(
    useAreaWorkspaceResourceAtom(href)
  );
  const { data: workspace, error: workspaceError } =
    formatResource(workspaceResource);

  // Undefined until the workspace says where the area is, so the atom doesn't fetch
  // until there is something to fetch. Writing the atom refetches: archiving and the
  // map flag edit the area in place, and this copy has to keep saying what is true.
  const [areaResource, refetchArea] = useAtom(
    useAreaWorkspaceAreaAtom(workspace?.detail?.area?.href)
  );
  const { data: area, error: areaError } = formatResource(areaResource);

  const loadError = workspaceError ?? areaError;

  if (loadError) {
    return <InlineNotification kind="error" title={loadError} lowContrast />;
  }

  if (!workspace || !area) {
    return <InlineLoading description="Loading workspace..." />;
  }

  return (
    <AreaWorkspaceContent
      workspace={workspace}
      area={area}
      onLeave={onLeave}
      onAreaChanged={refetchArea}
      onWorkspaceChanged={refetchWorkspace}
    />
  );
};
